// The locked editor's grid: drum lane (16 pads) + bass/harmony/lead/classic piano rolls showing
// only the scale's notes, plus the extra lanes a song adds ("+ Lane": another of the instrument's
// sounds next to the main one; × removes it with its notes). Click adds/removes, the first drag direction sets velocity
// (↕) or length (↔), Alt forces length, Shift snaps finely. Fully keyboard operable: arrows move
// the cursor, Enter toggles, +/− velocity, [ ] length.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  defaultMix,
  GRID_TICKS,
  laneSlot,
  loopSound,
  LOOP_BARS,
  LOOP_TICKS,
  FIRST_ATMOS,
  getAtmosKit,
  NUM_SOUNDS,
  scaleNotesInRange,
  slotRange,
  TICKS_PER_BAR,
  TICKS_PER_BEAT,
  type NoteEvent,
} from "@eb/shared";
import { audibleLoopTick } from "./audio";
import {
  buildRows,
  cellTick,
  dragLength,
  dragMode,
  dragVelocity,
  LABEL_W,
  noteLabel,
  rowAt,
  rowOfEvent,
  tickToX,
  type GridRow,
  type LaneRole,
  type View,
} from "./gridMath";
import { addableLanes, addLane, addNote, noteInCell, removeLane, removeNote, updateNote } from "./ops";
import { beginGesture, currentView, edit, editSong, endGesture, songKeyOf, useEditor, useSongKey } from "./store";
import { previewSound } from "./padInput";
import { useWizard } from "./wizardClient";

const LANE_COLOUR: Record<LaneRole, string> = {
  drums: "#009e73",
  bass: "#e69f00",
  harmony: "#56b4e9",
  lead: "#cc79a7",
  classic: "#f0e442",
  atmos: "#9b8cf2",
};
const LANE_NAME: Record<LaneRole, string> = {
  drums: "Drums",
  bass: "Bass",
  harmony: "Harmony",
  lead: "Lead",
  classic: "Classics",
  atmos: "Atmos & FX",
};

interface Drag {
  mode: "velocity" | "length" | "maybe-remove" | "new";
  ev: NoteEvent;
  startX: number;
  startY: number;
  moved: boolean;
}

export function TrackGrid() {
  const def = useEditor((s) => s.def);
  const spec = useEditor((s) => s.spec);
  const collapsed = useEditor((s) => s.collapsed);
  const zoomBars = useEditor((s) => s.zoomBars);
  const scrollBar = useEditor((s) => s.scrollBar);
  const grid = useEditor((s) => s.grid);
  const lanes = useEditor((s) => s.hist.present.lanes);
  const variants = useEditor((s) => s.hist.present.variants);
  const readOnly = useEditor((s) => s.readOnly);
  const [adding, setAdding] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const headRef = useRef<HTMLCanvasElement>(null);
  const [width, setWidth] = useState(900);
  const [cursor, setCursor] = useState({ row: 1, tick: 0 });
  const [announce, setAnnounce] = useState("");
  const drag = useRef<Drag | null>(null);

  const songKey = useSongKey();
  const { rows, height } = useMemo(
    () => (songKey ? buildRows(songKey, new Set(collapsed), lanes ?? []) : { rows: [] as GridRow[], height: 0 }),
    [songKey, collapsed, lanes],
  );
  const view: View = useMemo(() => {
    const noteW = Math.max(200, width - LABEL_W);
    return { startTick: scrollBar * TICKS_PER_BAR, ticksPerPx: (zoomBars * TICKS_PER_BAR) / noteW };
  }, [width, zoomBars, scrollBar]);
  const gridT = GRID_TICKS[grid];

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const draw = useCallback(() => {
    const c = canvasRef.current;
    if (!c || !spec) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    if (c.width !== Math.round(width * dpr) || c.height !== Math.round(height * dpr)) {
      c.width = Math.round(width * dpr);
      c.height = Math.round(height * dpr);
      c.style.width = `${width}px`;
      c.style.height = `${height}px`;
    }
    const g = c.getContext("2d")!;
    g.setTransform(dpr, 0, 0, dpr, 0, 0);
    g.clearRect(0, 0, width, height);
    const loop = currentView();
    const endTick = view.startTick + (width - LABEL_W) * view.ticksPerPx;
    // Rows.
    for (const r of rows) {
      if (r.kind === "header") {
        g.fillStyle = "#2e3546";
        g.fillRect(0, r.y, width, r.h);
        g.fillStyle = LANE_COLOUR[r.role];
        g.fillRect(0, r.y, r.slot >= NUM_SOUNDS ? 8 : 4, r.h);
        g.fillStyle = "#eef1f7";
        g.font = "600 13px -apple-system, system-ui, sans-serif";
        const folded = collapsed.includes(r.lane);
        const inst =
          r.role === "drums"
            ? ""
            : r.role === "atmos"
              ? ` · ${def ? getAtmosKit(def.atmosKit).name : ""}`
              : ` · ${loopSound(spec, loop.variants, r.slot)?.name ?? ""}`;
        const extra = r.slot >= NUM_SOUNDS ? " +" : "";
        const title = `${folded ? "▸" : "▾"} ${LANE_NAME[r.role]}${extra}${inst}`;
        g.fillText(title, 12, r.y + 19);
        const titleEnd = 12 + g.measureText(title).width + 8;
        // Bar numbers along each lane header (not over the lane's name).
        g.font = "600 11px ui-monospace, Menlo, monospace";
        g.fillStyle = "#9aa3b5";
        for (let bar = 0; bar < LOOP_BARS; bar++) {
          const bx = tickToX(bar * TICKS_PER_BAR, view);
          if (bx >= LABEL_W - 1 && bx + 5 >= titleEnd && bx < width - 10) g.fillText(String(bar + 1), bx + 5, r.y + 19);
        }
        continue;
      }
      const odd = rows.indexOf(r) % 2 === 0;
      g.fillStyle =
        r.kind === "note" && r.tonic ? "rgba(230,159,0,0.07)" : odd ? "rgba(255,255,255,0.025)" : "rgba(0,0,0,0.08)";
      g.fillRect(0, r.y, width, r.h);
      g.fillStyle = r.kind === "note" && r.tonic ? "#f3c35b" : "#9aa3b5";
      g.font = `${r.kind === "pad" ? 12 : 11}px -apple-system, system-ui, sans-serif`;
      const label =
        r.kind === "pad"
          ? `${r.lane === "atmos" ? r.slot - FIRST_ATMOS + 1 : r.slot + 1}  ${spec.sounds[r.slot].name}`
          : noteLabel(r.note);
      g.fillText(label, 12, r.y + r.h - 6);
    }
    // Grid lines.
    g.save();
    g.beginPath();
    g.rect(LABEL_W, 0, width - LABEL_W, height);
    g.clip();
    const step = gridT;
    for (let t = Math.floor(view.startTick / step) * step; t <= endTick; t += step) {
      const x = Math.round(tickToX(t, view)) + 0.5;
      g.fillStyle =
        t % TICKS_PER_BAR === 0
          ? "rgba(255,255,255,0.28)"
          : t % TICKS_PER_BEAT === 0
            ? "rgba(255,255,255,0.12)"
            : "rgba(255,255,255,0.045)";
      g.fillRect(x, 0, 1, height);
    }
    // Notes.
    for (const e of loop.events) {
      if (e.tick + e.length_ticks < view.startTick || e.tick > endTick) continue;
      const ri = rowOfEvent(rows, e.pad, e.note);
      if (ri < 0) continue;
      const r = rows[ri];
      const x = tickToX(e.tick, view);
      const w = Math.max(4, e.length_ticks / view.ticksPerPx - 1);
      g.globalAlpha = 0.45 + (e.velocity / 127) * 0.55;
      g.fillStyle = LANE_COLOUR[r.role];
      roundRect(g, x + 1, r.y + 2, w, r.h - 4, 4);
      g.globalAlpha = 1;
      // Velocity bar inside the note.
      g.fillStyle = "rgba(255,255,255,0.55)";
      g.fillRect(x + 2, r.y + r.h - 4, Math.max(2, (w - 2) * (e.velocity / 127)), 2);
    }
    // Wizard suggestion: dashed ghost notes.
    const sug = useWizard.getState().suggestion;
    if (sug) {
      g.setLineDash([4, 3]);
      g.lineWidth = 1.5;
      g.strokeStyle = "#f0b6d6";
      for (const e of sug.events) {
        if (e.tick + e.length_ticks < view.startTick || e.tick > endTick) continue;
        const ri = rowOfEvent(rows, e.pad, e.note);
        if (ri < 0) continue;
        const r = rows[ri];
        g.strokeRect(
          tickToX(e.tick, view) + 1.5,
          r.y + 2.5,
          Math.max(4, e.length_ticks / view.ticksPerPx - 2),
          r.h - 5,
        );
      }
      g.setLineDash([]);
    }
    // Keyboard cursor.
    const cr = rows[cursor.row];
    if (cr && cr.kind !== "header" && document.activeElement === wrapRef.current) {
      g.strokeStyle = "#f0e442";
      g.lineWidth = 2;
      g.strokeRect(tickToX(cursor.tick, view) + 1, cr.y + 1, gridT / view.ticksPerPx - 2, cr.h - 2);
    }
    g.restore();
    g.fillStyle = "rgba(0,0,0,0.25)";
    g.fillRect(LABEL_W - 1, 0, 1, height);
  }, [rows, height, width, view, spec, def, collapsed, cursor, gridT]);

  useEffect(() => {
    draw();
    const offEditor = useEditor.subscribe((s, p) => {
      if (s.hist.present !== p.hist.present || s.pattern !== p.pattern) draw();
    });
    const offWizard = useWizard.subscribe((s, p) => {
      if (s.suggestion !== p.suggestion) draw();
    });
    return () => {
      offEditor();
      offWizard();
    };
  }, [draw]);

  // Playhead on its own canvas layer, redrawn every frame.
  useEffect(() => {
    let raf = 0;
    const frame = () => {
      const c = headRef.current;
      if (c) {
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        if (c.width !== Math.round(width * dpr) || c.height !== Math.round(height * dpr)) {
          c.width = Math.round(width * dpr);
          c.height = Math.round(height * dpr);
          c.style.width = `${width}px`;
          c.style.height = `${height}px`;
        }
        const g = c.getContext("2d")!;
        g.setTransform(dpr, 0, 0, dpr, 0, 0);
        g.clearRect(0, 0, width, height);
        const tick = audibleLoopTick();
        if (tick !== null) {
          const x = tickToX(tick, view);
          if (x >= LABEL_W) {
            g.fillStyle = "#f0e442";
            g.shadowColor = "rgba(240,228,66,0.8)";
            g.shadowBlur = 8;
            g.fillRect(x - 1, 0, 2, height);
          }
        }
      }
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [width, height, view]);

  const local = (e: React.PointerEvent) => {
    const r = canvasRef.current!.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!def || e.button !== 0) return;
    const { x, y } = local(e);
    const ri = rowAt(rows, y);
    if (ri < 0) return;
    const row = rows[ri];
    if (row.kind === "header") {
      const s = useEditor.getState();
      s.set({
        collapsed: s.collapsed.includes(row.lane)
          ? s.collapsed.filter((l) => l !== row.lane)
          : [...s.collapsed, row.lane],
      });
      return;
    }
    useEditor.getState().set({ selectedSlot: row.slot });
    if (x < LABEL_W) {
      previewSound(row.slot, row.kind === "note" ? row.note : -1, 100);
      return;
    }
    const loop = currentView();
    const note = row.kind === "note" ? row.note : 60;
    // Grabbing a note's right end resizes it.
    const end = loop.events.find((n) => {
      if (n.pad !== row.slot || (row.kind === "note" && n.note !== note)) return false;
      const xe = tickToX(n.tick + n.length_ticks, view);
      return Math.abs(x - xe) <= 5 && x > tickToX(n.tick, view) + 4;
    });
    canvasRef.current!.setPointerCapture(e.pointerId);
    if (end) {
      beginGesture("Note length");
      drag.current = { mode: "length", ev: end, startX: x, startY: y, moved: false };
      return;
    }
    const tick = cellTick(x, view, gridT);
    if (tick < 0 || tick >= LOOP_TICKS) return;
    const existing = noteInCell(loop.events, row.slot, note, tick, gridT);
    beginGesture("Edit note");
    if (existing) {
      drag.current = { mode: e.altKey ? "length" : "maybe-remove", ev: existing, startX: x, startY: y, moved: false };
    } else {
      const ev: NoteEvent = {
        tick,
        pad: row.slot,
        note,
        velocity: 100,
        // Atmos pads are long sounds: a bar to start with (drag to change).
        length_ticks: row.kind === "note" ? gridT * 2 : row.lane === "atmos" ? 3840 : gridT,
      };
      edit((l) => addNote(l, ev, songKeyOf(useEditor.getState()) ?? def.key));
      previewSound(row.slot, row.kind === "note" ? note : -1, 100);
      drag.current = { mode: e.altKey ? "length" : "new", ev, startX: x, startY: y, moved: false };
    }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    if (!d) {
      // Cursor hint: resize handle near note ends.
      return;
    }
    const { x, y } = local(e);
    const dx = x - d.startX;
    const dy = y - d.startY;
    if (!d.moved) {
      const m = dragMode(dx, dy, e.altKey);
      if (!m) return;
      d.moved = true;
      if (d.mode === "maybe-remove" || d.mode === "new") d.mode = m;
    }
    if (d.mode === "velocity") {
      const v = dragVelocity(d.ev.velocity, dy);
      edit((l) => updateNote(l, d.ev, { velocity: v }));
    } else if (d.mode === "length") {
      const len = dragLength(d.ev.length_ticks, dx, view.ticksPerPx, gridT, e.shiftKey);
      edit((l) => updateNote(l, d.ev, { length_ticks: len }));
    }
  };

  const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const d = drag.current;
    drag.current = null;
    if (canvasRef.current?.hasPointerCapture(e.pointerId)) canvasRef.current.releasePointerCapture(e.pointerId);
    if (!d) return;
    if (d.mode === "maybe-remove" && !d.moved) edit((l) => removeNote(l, d.ev));
    endGesture();
  };

  // ---------------------------------------------------------------- keyboard

  const describe = (ri: number, tick: number) => {
    const r = rows[ri];
    if (!r || r.kind === "header" || !spec) return "";
    const bar = Math.floor(tick / TICKS_PER_BAR) + 1;
    const stepInBar = Math.floor((tick % TICKS_PER_BAR) / gridT) + 1;
    const what =
      r.kind === "pad"
        ? spec.sounds[r.slot].name
        : `${LANE_NAME[r.role]}${r.slot >= NUM_SOUNDS ? ` (${loopSound(spec, undefined, r.slot)?.name})` : ""} ${noteLabel(r.note)}`;
    const n = noteInCell(currentView().events, r.slot, r.kind === "note" ? r.note : 60, tick, gridT);
    return `${what}, bar ${bar} step ${stepInBar}: ${n ? `note, velocity ${n.velocity}` : "empty"}`;
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!def) return;
    let { row, tick } = cursor;
    const r = rows[row];
    const loop = currentView();
    const note = r?.kind === "note" ? r.note : 60;
    const here = r && r.kind !== "header" ? noteInCell(loop.events, r.slot, note, tick, gridT) : undefined;
    const moveRow = (d: number) => {
      let i = row + d;
      while (rows[i] && rows[i].kind === "header") i += d;
      if (rows[i]) row = i;
    };
    switch (e.key) {
      case "ArrowUp":
        moveRow(-1);
        break;
      case "ArrowDown":
        moveRow(1);
        break;
      case "ArrowLeft":
        tick = Math.max(0, tick - gridT);
        break;
      case "ArrowRight":
        tick = Math.min(LOOP_TICKS - gridT, tick + gridT);
        break;
      case "Enter":
      case " ":
        if (!r || r.kind === "header") return;
        if (here) edit((l) => removeNote(l, here));
        else {
          edit((l) =>
            addNote(
              l,
              {
                tick,
                pad: r.slot,
                note,
                velocity: 100,
                length_ticks: r.kind === "note" ? gridT * 2 : r.lane === "atmos" ? 3840 : gridT,
              },
              songKeyOf(useEditor.getState()) ?? def.key,
            ),
          );
          previewSound(r.slot, r.kind === "note" ? note : -1, 100);
        }
        break;
      case "+":
      case "=":
        if (here) edit((l) => updateNote(l, here, { velocity: here.velocity + 8 }));
        break;
      case "-":
        if (here) edit((l) => updateNote(l, here, { velocity: here.velocity - 8 }));
        break;
      case "]":
        if (here) edit((l) => updateNote(l, here, { length_ticks: here.length_ticks + gridT }));
        break;
      case "[":
        if (here) edit((l) => updateNote(l, here, { length_ticks: Math.max(gridT / 4, here.length_ticks - gridT) }));
        break;
      default:
        return;
    }
    e.preventDefault();
    // Keep the cursor in view.
    const s = useEditor.getState();
    const bar = Math.floor(tick / TICKS_PER_BAR);
    if (bar < s.scrollBar || bar >= s.scrollBar + s.zoomBars)
      s.set({
        scrollBar: Math.min(LOOP_BARS - s.zoomBars, Math.max(0, bar - (bar < s.scrollBar ? 0 : s.zoomBars - 1))),
      });
    setCursor({ row, tick });
    setAnnounce(describe(row, tick));
  };

  return (
    <div
      className="track-grid"
      ref={wrapRef}
      tabIndex={0}
      role="application"
      aria-roledescription="pattern grid"
      aria-label="Pattern grid. Arrow keys move, Enter adds or removes a note, plus and minus change velocity, brackets change length."
      onKeyDown={onKeyDown}
      onFocus={() => {
        draw();
        setAnnounce(describe(cursor.row, cursor.tick));
      }}
      onBlur={draw}
      data-testid="track-grid"
    >
      <div className="track-grid-inner" style={{ height }}>
        <canvas
          ref={canvasRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          style={{ touchAction: "none" }}
        />
        <canvas ref={headRef} className="grid-playhead" aria-hidden="true" />
        {spec &&
          !readOnly &&
          rows.map((r) => {
            if (r.kind !== "header" || r.role === "drums" || r.role === "atmos") return null;
            if (r.slot >= NUM_SOUNDS) {
              const name = loopSound(spec, undefined, r.slot)?.name ?? "";
              return (
                <div key={r.lane} className="lane-tools" style={{ top: r.y + 4 }}>
                  <button
                    type="button"
                    className="btn btn-small btn-ghost"
                    title={`Remove this ${LANE_NAME[r.role]} lane (${name}) and its notes`}
                    aria-label={`Remove the extra ${LANE_NAME[r.role]} lane ${name}`}
                    data-testid={`lane-remove-${r.slot}`}
                    onClick={() => {
                      editSong((l) => removeLane(l, r.slot));
                      const st = useEditor.getState();
                      if (st.selectedSlot === r.slot) st.set({ selectedSlot: laneSlot(r.slot) });
                      if (st.padTarget === r.slot) st.set({ padTarget: laneSlot(r.slot) });
                    }}
                  >
                    ×
                  </button>
                </div>
              );
            }
            const can = addableLanes({ events: [], mix: [], lanes, variants }, r.slot);
            if (!can.length) return null;
            return (
              <div key={r.lane} className="lane-tools" style={{ top: r.y + 4 }}>
                {adding === r.slot ? (
                  <>
                    {can.map((p) => (
                      <button
                        key={p}
                        type="button"
                        className="btn btn-small"
                        title={`Add a ${LANE_NAME[r.role]} lane playing ${loopSound(spec, undefined, p)?.name}`}
                        data-testid={`lane-add-${p}`}
                        onClick={() => {
                          editSong((l) => addLane(l, p, defaultMix(spec)));
                          setAdding(null);
                          if (def) {
                            const range = slotRange(p)!;
                            const notes = scaleNotesInRange(
                              songKeyOf(useEditor.getState()) ?? def.key,
                              range.lo,
                              range.hi,
                            );
                            previewSound(p, notes[Math.floor(notes.length / 3)] ?? range.lo, 100);
                          }
                        }}
                      >
                        + {loopSound(spec, undefined, p)?.name}
                      </button>
                    ))}
                    <button
                      type="button"
                      className="btn btn-small btn-ghost"
                      aria-label="Cancel"
                      onClick={() => setAdding(null)}
                    >
                      ×
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-small btn-ghost"
                    title={`Add another ${LANE_NAME[r.role]} lane with one of today's other ${LANE_NAME[r.role]} sounds`}
                    data-testid={`lane-add-${r.role}`}
                    onClick={() => setAdding(r.slot)}
                  >
                    + Lane
                  </button>
                )}
              </div>
            );
          })}
      </div>
      <div className="sr-only" aria-live="polite">
        {announce}
      </div>
    </div>
  );
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath();
  g.roundRect(x, y, w, h, Math.min(r, w / 2, h / 2));
  g.fill();
}
